import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Card, Button, Alert, Form, Row, Col, Badge } from 'react-bootstrap';
import { IconPlus, IconPencil, IconTrash, IconCheck, IconX, IconSparkles, IconNote, IconGripVertical } from '@tabler/icons-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { api } from '../../../shared/api';
import { getBaseUrl } from '../../../shared/api';
import { useTranslation } from 'react-i18next';
import { useDateFormat } from '../../../shared/DateFormatContext';
import { useConfirm } from '../../../shared/ConfirmContext';
import { usePermissions } from '../../../shared/permissions';
import { useInputHistory } from '../../../shared/useInputHistory';
import { stringToColor, getContrastColor } from '../../../shared/colorUtils';
import { useRealtimeEvents } from '../../../shared/useRealtimeEvents';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import EmptyState from '../../../components/feedback/EmptyState';
import PageSection from '../../../components/layout/PageSection';

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Whisper hook — calls /api/assistant/api/whisper/ and returns a suggestion
// ---------------------------------------------------------------------------
function useWhisper(projectName) {
  const [suggestion, setSuggestion] = useState('');
  const timerRef = useRef(null);
  const abortRef = useRef(null);

  const trigger = useCallback((text) => {
    setSuggestion('');
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!text || text.length < 20) return;  // need enough text to complete

    timerRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      try {
        const resp = await fetch(`${getBaseUrl()}/assistant/api/whisper/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ text, project_name: projectName }),
          credentials: 'include',
          signal: ctrl.signal,
        });
        if (resp.ok) {
          const data = await resp.json();
          setSuggestion(data.suggestion || '');
        }
      } catch (e) {
        if (e.name !== 'AbortError') {
          setSuggestion('');
        }
      }
    }, 700);
  }, [projectName]);

  const clear = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setSuggestion('');
  }, []);

  return { suggestion, trigger, clear };
}

// ---------------------------------------------------------------------------
// NoteEditor — textarea with ghost-text whisper overlay + history suggestions
// ---------------------------------------------------------------------------
function NoteEditor({ value, onChange, projectName, placeholder, historyKey }) {
  const textareaRef = useRef(null);
  const { suggestion, trigger, clear } = useWhisper(projectName);
  const noteHistory = useInputHistory(historyKey || 'project_note_history');
  const { t } = useTranslation();
  const [historySugg, setHistorySugg] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // Adjust textarea height to content
  const autoGrow = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  };

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    trigger(v);
    autoGrow(e.target);
    // update history dropdown
    const sugg = noteHistory.getSuggestions(v);
    setHistorySugg(sugg);
    setShowHistory(sugg.length > 0);
  };

  const handleFocus = () => {
    const sugg = noteHistory.getSuggestions(value);
    setHistorySugg(sugg);
    setShowHistory(sugg.length > 0);
  };

  const handlePickHistory = (text) => {
    onChange(text);
    setShowHistory(false);
    setTimeout(() => autoGrow(textareaRef.current), 0);
    textareaRef.current?.focus();
  };

  const handleRemoveSuggestion = (e, text) => {
    e.preventDefault();
    e.stopPropagation();
    noteHistory.remove(text);
    const updated = historySugg.filter(s => s !== text);
    setHistorySugg(updated);
    if (updated.length === 0) setShowHistory(false);
  };

  const handleKeyDown = (e) => {
    // Tab accepts the whisper suggestion
    if (e.key === 'Tab' && suggestion) {
      e.preventDefault();
      onChange(value + suggestion);
      clear();
      setShowHistory(false);
      setTimeout(() => autoGrow(textareaRef.current), 0);
    }
    if (e.key === 'Escape') setShowHistory(false);
  };

  // Ghosted overlay text = current value + suggestion (suggestion portion greyed out)
  const overlayText = suggestion ? value + suggestion : '';

  return (
    <div style={{ position: 'relative' }}>
      {/* Ghost text overlay (pointer-events:none so clicks go to textarea) */}
      {suggestion && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            padding: '0.375rem 0.75rem',
            fontFamily: 'inherit',
            fontSize: 'inherit',
            lineHeight: 'inherit',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            pointerEvents: 'none',
            color: 'transparent',
            borderColor: 'transparent',
            backgroundColor: 'transparent',
            overflow: 'hidden',
          }}
        >
          {value}
          <span style={{ color: 'var(--tblr-body-color-secondary)', opacity: 0.6 }}>
            {suggestion}
          </span>
        </div>
      )}
      <Form.Control
        as="textarea"
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { trigger(value); handleFocus(); }}
        onBlur={() => { clear(); setTimeout(() => setShowHistory(false), 150); }}
        placeholder={placeholder || t('projects.notes.editorPlaceholder')}
        style={{
          minHeight: 90,
          resize: 'vertical',
          background: 'transparent',
          position: 'relative',
          zIndex: 1,
        }}
      />
      {showHistory && historySugg.length > 0 && (
        <div className="task-history-dropdown">
          {historySugg.map((s, i) => (
            <div
              key={i}
              className="task-history-item"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}
            >
              <span
                style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
                onMouseDown={() => handlePickHistory(s)}
              >
                {s.length > 80 ? s.slice(0, 80) + '…' : s}
              </span>
              <span
                title={t('projects.notes.removeSuggestion')}
                onMouseDown={(e) => handleRemoveSuggestion(e, s)}
                style={{ cursor: 'pointer', color: 'var(--tblr-danger)', padding: '0 2px', flexShrink: 0, fontSize: '13px', lineHeight: 1 }}
              >
                ×
              </span>
            </div>
          ))}
        </div>
      )}
      {suggestion && (
        <div style={{ fontSize: '11px', color: 'var(--tblr-body-color-secondary)', marginTop: 2 }}>
          <IconSparkles size={11} style={{ marginRight: 3 }} />
          {t('projects.notes.acceptSuggestion')}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main ProjectNotes component
// ---------------------------------------------------------------------------
const ProjectNotes = ({ projectId, projectName }) => {
  const { t } = useTranslation();
  const { formatDateTime } = useDateFormat();
  const confirm = useConfirm();
  const { canManage } = usePermissions();
  const noteHistory = useInputHistory('project_note_history');

  const queryClient = useQueryClient();
  const [notes, setNotes] = useState([]);
  const [error, setError] = useState(null);

  // New note form
  const [showForm, setShowForm] = useState(false);
  const [formContent, setFormContent] = useState('');
  const [saving, setSaving] = useState(false);

  // Editing existing note
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');

  const noteTemplates = [
    { label: t('projects.notes.templates.transport.label'), text: t('projects.notes.templates.transport.text') },
    { label: t('projects.notes.templates.technical.label'), text: t('projects.notes.templates.technical.text') },
    { label: t('projects.notes.templates.accommodation.label'), text: t('projects.notes.templates.accommodation.text') },
    { label: t('projects.notes.templates.catering.label'), text: t('projects.notes.templates.catering.text') },
    { label: t('projects.notes.templates.contact.label'), text: t('projects.notes.templates.contact.text') },
    { label: t('projects.notes.templates.security.label'), text: t('projects.notes.templates.security.text') },
  ];

  const { isLoading: loading, data: queryData } = useQuery({
    queryKey: ['project-notes', projectId],
    queryFn: async () => {
      const data = await api.get(`projects/api/project-notes/?project=${projectId}`);
      const list = data.results ?? data;
      setNotes(list);
      list.forEach(n => { if (n.content) noteHistory.add(n.content); });
      return list;
    },
  });

  useEffect(() => {
    if (!queryData) return;
    setNotes(queryData || []);
    queryData.forEach(n => { if (n.content) noteHistory.add(n.content); });
  }, [queryData, noteHistory]);

  const fetchNotes = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['project-notes', projectId] });
  }, [queryClient, projectId]);

  // ── realtime updates ─────────────────────────────────────────────────────
  const _notesDebounce = useRef(null);
  useRealtimeEvents(useCallback((e) => {
    if (e.model !== 'projects.projectnote') return;
    clearTimeout(_notesDebounce.current);
    _notesDebounce.current = setTimeout(() => fetchNotes(), 400);
  }, [fetchNotes])); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  const handleCreate = async () => {
    if (!formContent.trim()) return;
    setSaving(true);
    try {
      await api.post('projects/api/project-notes/', { project: projectId, content: formContent.trim() });
      noteHistory.add(formContent.trim());
      setFormContent('');
      setShowForm(false);
      fetchNotes();
    } catch {
      setError(t('projects.notes.errorSaving'));
    } finally {
      setSaving(false);
    }
  };

  const handleEditSave = async (note) => {
    if (!editContent.trim()) return;
    try {
      await api.patch(`projects/api/project-notes/${note.id}/`, { content: editContent.trim() });
      noteHistory.add(editContent.trim());
      setEditingId(null);
      fetchNotes();
    } catch {
      setError(t('projects.notes.errorUpdating'));
    }
  };

  const handleDelete = async (note) => {
    const ok = await confirm(t('projects.notes.confirmDelete'));
    if (!ok) return;
    try {
      await api.delete(`projects/api/project-notes/${note.id}/`);
      fetchNotes();
    } catch {
      setError(t('projects.notes.errorDeleting'));
    }
  };

  const handleDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination || destination.index === source.index) return;

    const reordered = Array.from(notes);
    const [moved] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, moved);
    setNotes(reordered);

    try {
      await api.post('projects/api/project-notes/bulk_update_priority/', {
        note_priorities: reordered.map((n, i) => ({ id: n.id, priority: (i + 1) * 1000 })),
      });
      await queryClient.invalidateQueries({ queryKey: ['project-notes', projectId] });
    } catch {
      setError(t('projects.notes.errorReordering'));
      fetchNotes();
    }
  };

  // ---------------------------------------------------------------------------
  const insertTemplate = (tpl) => {
    setShowForm(true);
    setFormContent(prev => (prev ? prev + '\n\n' + tpl.text : tpl.text));
  };

  // ---------------------------------------------------------------------------
  if (loading) {
    return <div className="text-center py-4 text-secondary">{t('projects.notes.loading')}</div>;
  }

  return (
    <PageSection
      title={t('projects.notes.title')}
      description={t('projects.notes.description')}
      actions={!showForm && canManage ? (
        <Button size="sm" variant="outline-primary" onClick={() => setShowForm(true)}>
          <IconPlus size={14} className="me-1" />
          {t('projects.notes.add')}
        </Button>
      ) : null}
    >
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {/* Template chips */}
      <div className="mb-3 d-flex flex-wrap gap-1">
        <small className="text-secondary me-1 align-self-center">{t('projects.notes.templatesLabel')}</small>
        {noteTemplates.map((tpl) => {
          const bg = stringToColor(tpl.label);
          const color = getContrastColor(bg);
          return (
            <Button
              key={tpl.label}
              type="button"
              size="sm"
              variant="light"
              className="border-0"
              style={{ fontWeight: 'normal', fontSize: '12px', backgroundColor: bg, color }}
              onClick={() => insertTemplate(tpl)}
            >
              {tpl.label}
            </Button>
          );
        })}
      </div>

      {/* New note form */}
      {showForm ? (
        <Card className="mb-3 app-surface border-primary-subtle">
          <Card.Body>
            <NoteEditor
              value={formContent}
              onChange={setFormContent}
              projectName={projectName}
              placeholder={t('projects.notes.editorPlaceholder')}
              historyKey="project_note_history"
            />
            <div className="d-flex gap-2 mt-2">
              <Button size="sm" variant="primary" onClick={handleCreate} disabled={saving || !formContent.trim()}>
                <IconCheck size={14} className="me-1" />
                {saving ? t('projects.notes.saving') : t('common.save')}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => { setShowForm(false); setFormContent(''); }}>
                <IconX size={14} className="me-1" />{t('common.cancel')}
              </Button>
            </div>
          </Card.Body>
        </Card>
      ) : null}

      {/* Notes list */}
      {notes.length === 0 && !showForm ? (
        <EmptyState
          title={t('projects.notes.emptyTitle')}
          message={t('projects.notes.emptyMessage')}
          action={canManage ? (
            <Button variant="primary" onClick={() => setShowForm(true)}>
              <IconPlus size={16} className="me-2" />
              {t('projects.notes.add')}
            </Button>
          ) : null}
        />
      ) : (
        <>
          {canManage && notes.length > 1 && (
            <div className="text-secondary mb-2" style={{ fontSize: '11px' }}>
              <IconGripVertical size={12} className="me-1" />
              {t('projects.notes.dragHint')}
            </div>
          )}
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="project-notes" isDropDisabled={!canManage || editingId !== null}>
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {notes.map((note, index) => (
                    <Draggable
                      key={note.id}
                      draggableId={`note-${note.id}`}
                      index={index}
                      isDragDisabled={!canManage || editingId !== null}
                    >
                      {(dragProvided, dragSnapshot) => (
                        <Card
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          className="mb-2 app-surface"
                          style={{
                            ...dragProvided.draggableProps.style,
                            boxShadow: dragSnapshot.isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : undefined,
                          }}
                        >
                          <Card.Body className="py-2 px-3">
                            {editingId === note.id ? (
                              <>
                                <NoteEditor
                                  value={editContent}
                                  onChange={setEditContent}
                                  projectName={projectName}
                                  placeholder={t('projects.notes.editorPlaceholder')}
                                  historyKey="project_note_history"
                                />
                                <div className="btn-group btn-group-sm mt-2">
                                  <Button size="sm" variant="primary" onClick={() => handleEditSave(note)} disabled={!editContent.trim()}>
                                    <IconCheck size={14} className="me-1" />{t('common.save')}
                                  </Button>
                                  <Button size="sm" variant="secondary" onClick={() => setEditingId(null)}>
                                    <IconX size={14} className="me-1" />{t('common.cancel')}
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <Row className="align-items-start">
                                {canManage && (
                                  <Col xs="auto" className="pe-0">
                                    <div
                                      {...dragProvided.dragHandleProps}
                                      className="text-secondary"
                                      style={{ cursor: 'grab', paddingTop: 2 }}
                                      title={t('projects.notes.dragHint')}
                                    >
                                      <IconGripVertical size={16} />
                                    </div>
                                  </Col>
                                )}
                                <Col>
                                  <div style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>{note.content}</div>
                                  <div className="text-secondary" style={{ fontSize: '11px', marginTop: 4 }}>
                                    {note.author_name && <span className="me-2">{note.author_name}</span>}
                                    {formatDateTime(note.created_at)}
                                    {note.updated_at !== note.created_at && (
                                      <span className="ms-2">({t('projects.notes.updatedAt')} {formatDateTime(note.updated_at)})</span>
                                    )}
                                  </div>
                                </Col>
                                {canManage && (
                                  <Col xs="auto">
                                    <div className="btn-group btn-group-sm">
                                    <Button
                                      size="sm"
                                      variant="outline-secondary"
                                      onClick={() => { setEditingId(note.id); setEditContent(note.content); }}
                                      title={t('common.edit')}
                                    >
                                      <IconPencil size={14} className="me-1" />
                                      {t('common.edit')}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() => handleDelete(note)}
                                      title={t('common.delete')}
                                    >
                                      <IconTrash size={14} className="me-1" />
                                      {t('common.delete')}
                                    </Button>
                                    </div>
                                  </Col>
                                )}
                              </Row>
                            )}
                          </Card.Body>
                        </Card>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </>
      )}
    </PageSection>
  );
};

export default ProjectNotes;
